import { useState, useEffect, useRef } from 'react';
import {
  Box, Button, Typography, CircularProgress, Alert,
  ToggleButton, ToggleButtonGroup, Paper, Stack,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { vortxColors } from '@vortx/shared-ui';
const PHD_API_BASE = process.env.REACT_APP_PHD_API_URL || 'http://localhost:5002';
import AgentDiagram from './AgentDiagram.jsx';
import TuningExplainer from './TuningExplainer.jsx';

const PROGRESS_MESSAGES = [
  'Running Tier 1 analysis...',
  'Building site profile...',
  'Running Tier 2 calibration sweep...',
  'Evaluating parameter combinations...',
  'Finalizing tuned configuration...',
];

/**
 * MetaTuningStep — Calls PhD API to run meta-controller tuning,
 * then displays coordination topology and parameter explanations.
 */
export default function MetaTuningStep({ scenario_data, onComplete, onBack }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [progressIdx, setProgressIdx] = useState(0);
  const [view, setView] = useState('tuned');
  const intervalRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    // Rotate progress messages while loading
    intervalRef.current = setInterval(() => {
      setProgressIdx(prev => Math.min(prev + 1, PROGRESS_MESSAGES.length - 1));
    }, 40_000);

    async function runTuning() {
      try {
        console.log('MetaTuningStep scenario_data:', scenario_data ? `${JSON.stringify(scenario_data).length} bytes, agents: ${scenario_data.agents?.length}` : 'NULL');
        if (!scenario_data) {
          setError('No scenario data available — go back to Step 2 and rebuild the digital twin.');
          setLoading(false);
          return;
        }
        const response = await fetch(`${PHD_API_BASE}/api/calibration/tune`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ scenario_data }),
        });

        if (!response.ok) {
          const body = await response.json().catch(() => ({}));
          throw new Error(body.error || `Tuning failed (${response.status})`);
        }

        const data = await response.json();
        if (!cancelled) {
          setResult(data);
          setLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message);
          setLoading(false);
        }
      }
    }

    runTuning();

    return () => {
      cancelled = true;
      clearInterval(intervalRef.current);
    };
  }, [scenario_data]);

  useEffect(() => {
    if (!loading) {
      clearInterval(intervalRef.current);
    }
  }, [loading]);

  const handleContinue = () => {
    if (!result) return;
    onComplete({
      tier1_config: result.tier1_config,
      tier2_config: result.tier2_config,
      explanations: result.explanation || [],
      site_profile: result.site_profile,
    });
  };

  // --- Loading state ---
  if (loading) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <CircularProgress size={64} sx={{ color: vortxColors.gold, mb: 3 }} />
        <Typography variant="h6" sx={{ mb: 1 }}>
          Tuning Meta-Controller
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 2 }}>
          {PROGRESS_MESSAGES[progressIdx]}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          This typically takes 3-8 minutes
        </Typography>
      </Box>
    );
  }

  // --- Error state ---
  if (error) {
    return (
      <Box sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={onBack}>
          Back
        </Button>
      </Box>
    );
  }

  // --- Success state ---
  const {
    tier1_config = {},
    tier2_config = {},
    explanation = [],
    site_profile = {},
  } = result || {};

  const activeConfig = view === 'tuned' ? tier2_config : tier1_config;

  // Build agent list from scenario_data or site_profile flags
  const agentTypes = [];
  if (site_profile.has_battery) agentTypes.push('battery');
  if (site_profile.has_ev) agentTypes.push('ev');
  if (site_profile.has_cold) agentTypes.push('cold');
  // Fallback: check scenario_data.agents directly
  const agents = agentTypes.length > 0
    ? agentTypes
    : (scenario_data?.agents || []).map(a => a.type);

  const coordinationConfig = {
    coordination_mode: activeConfig.coordination_mode,
    agent_dc_fraction: activeConfig.agent_dc_fraction,
    flexibility_threshold: activeConfig.flexibility_threshold ?? 0.7,
    use_proportional_dc: activeConfig.use_proportional_dc,
  };

  // Build full parameter table (show all params, not just diffs)
  const allParams = [
    { parameter: 'Coordination Mode', default_value: tier1_config.coordination_mode, tuned_value: tier2_config.coordination_mode },
    { parameter: 'DC Allocation', default_value: tier1_config.agent_dc_fraction, tuned_value: tier2_config.agent_dc_fraction },
    { parameter: 'Flexibility Threshold', default_value: tier1_config.flexibility_threshold, tuned_value: tier2_config.flexibility_threshold },
    { parameter: 'Max Passes', default_value: tier1_config.max_passes, tuned_value: tier2_config.max_passes },
    { parameter: 'MILP Time Limit', default_value: tier1_config.milp_time_limit_s, tuned_value: tier2_config.milp_time_limit_s },
    { parameter: 'Proportional DC', default_value: tier1_config.use_proportional_dc ? 'Yes' : 'No', tuned_value: tier2_config.use_proportional_dc ? 'Yes' : 'No' },
  ];

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
        Meta-Controller Tuning Results
      </Typography>

      {/* View toggle */}
      <ToggleButtonGroup
        value={view}
        exclusive
        onChange={(_, val) => { if (val) setView(val); }}
        size="small"
        sx={{ mb: 3 }}
      >
        <ToggleButton value="default">Default</ToggleButton>
        <ToggleButton value="tuned">Site-Tuned</ToggleButton>
      </ToggleButtonGroup>

      {/* Top section: diagram + explainer side by side */}
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={3}
        sx={{ mb: 4 }}
      >
        <Paper sx={{ p: 2, flex: 1, minWidth: 0 }}>
          <AgentDiagram
            agents={agents}
            coordinationConfig={coordinationConfig}
          />
        </Paper>

        <Paper sx={{ p: 2, flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, color: vortxColors.stone }}>
            Algorithm Parameters
          </Typography>
          <TuningExplainer explanations={allParams} diffs={explanation} />
        </Paper>
      </Stack>

      {/* TODO: Bottom section — representative 24h dispatch chart.
         Will use PowerGraph from @vortx/shared-ui once dispatch data
         is available from the simulation in Step 4. */}

      {/* Navigation */}
      <Stack direction="row" justifyContent="space-between">
        <Button startIcon={<ArrowBackIcon />} onClick={onBack}>
          Back
        </Button>
        <Button
          variant="contained"
          endIcon={<ArrowForwardIcon />}
          onClick={handleContinue}
          sx={{
            backgroundColor: vortxColors.gold,
            '&:hover': { backgroundColor: vortxColors.goldHover },
          }}
        >
          Continue to Prediction Test
        </Button>
      </Stack>
    </Box>
  );
}
